import React from 'react'

/**
 * Editorial tab bar. Plain text tabs separated by a hair-rule, with a
 * heavier underline under the active one. No pill backgrounds — this is
 * meant to read like a newspaper section selector, not a SaaS UI.
 *
 * Controlled component: the parent owns the active tab id. Wiring the
 * value to the URL (`?tab=…`) is the parent's job — that keeps this
 * primitive routing-agnostic and easy to test in isolation.
 */
export interface TabDef {
  id: string
  label: string
  /** Optional small badge shown after the label, e.g. counts. */
  count?: number | null
  /** Optional tone for the count badge. Defaults to a muted style. */
  tone?: 'neutral' | 'signal'
}

interface TabsProps {
  tabs: TabDef[]
  active: string
  onChange: (id: string) => void
  /** ARIA label for the tablist. */
  ariaLabel?: string
  /** Tighter tab row (company profile). */
  dense?: boolean
  className?: string
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  active,
  onChange,
  ariaLabel,
  dense = false,
  className = '',
}) => {
  const pad = dense ? 'px-2 sm:px-3 py-2' : 'px-3 sm:px-4 py-3'
  const labelClass = dense ? 'font-serif text-sm' : 'font-serif text-base'

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`border-b border-rule -mx-1 flex flex-wrap ${dense ? 'mt-3' : 'mt-6'} ${className}`}
    >
      {tabs.map((t) => {
        const isActive = t.id === active
        return (
          <button
            key={t.id}
            role="tab"
            id={`tab-${t.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${t.id}`}
            tabIndex={isActive ? 0 : -1}
            type="button"
            onClick={() => onChange(t.id)}
            className={`relative ${pad} text-sm tracking-wide transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-400 ${
              isActive ? 'text-ink' : 'text-ink-500 hover:text-ink'
            }`}
          >
            <span className={labelClass}>{t.label}</span>
            {typeof t.count === 'number' && (
              <span
                className={`ml-1.5 inline-flex items-center px-1 py-px text-xs tabular-nums rounded-sm ${
                  t.tone === 'signal'
                    ? 'bg-signal-50 text-signal-900'
                    : 'bg-ink-50 text-ink-700'
                }`}
              >
                {t.count}
              </span>
            )}
            {isActive && (
              <span
                aria-hidden
                className="absolute left-0 right-0 -bottom-px h-0.5 bg-signal"
              />
            )}
          </button>
        )
      })}
    </div>
  )
}

export default Tabs
